import { type IntlShape, useIntl } from 'react-intl';

import PlansScreen from './PlansScreen';

import styles from './PlansScreenAdapter.module.scss';
import { useAppDispatch, useAppSelector } from '@/store/new-order';
import { selectPlanAndProceed } from '@/store/new-order/actions/planSelect';

import { type Plan } from '@/utils/mapProductObject';

export const resolveDiscountLabel = (intl: IntlShape, plans: Plan[], couponDiscountPercentage?: number | string) => {
  const specialOfferPlan = plans.find((p) => p.showSpecialOfferLabel);

  if (!specialOfferPlan && couponDiscountPercentage) {
    return intl.formatMessage(
      {
        id: 'plansScreen.discountLabel',
        defaultMessage:
          'You received a <discount>discount {discountPercentage, number}%</discount> on the first order!',
        description: 'Screen-wide discount label of the Plan selection screen',
      },
      {
        discountPercentage: couponDiscountPercentage,
        discount: (chunks) => <span className={styles.discountHighlight}>{chunks}</span>,
      },
    );
  }
  if (specialOfferPlan && couponDiscountPercentage) {
    return intl.formatMessage(
      {
        id: 'plansScreen.specialOfferLabelWithPercentage',
        defaultMessage:
          'Grab your personal discount {discountPercentage, number}% on {planName} plan on the first order!',
        description: 'Screen-wide discount label of the Plan selection screen with discount percentage',
      },
      {
        discountPercentage: couponDiscountPercentage,
        planName: specialOfferPlan.name[intl.locale === 'en' || intl.locale === 'uk' ? intl.locale : 'en'],
      },
    );
  }
  if (specialOfferPlan && !couponDiscountPercentage) {
    return intl.formatMessage(
      {
        id: 'plansScreen.specialOfferLabelWithoutPercentage',
        defaultMessage: 'Grab your personal discount on {planName} plan on the first order!',
        description: 'Screen-wide discount label of the Plan selection screen',
      },
      {
        planName: specialOfferPlan.name[intl.locale === 'en' || intl.locale === 'uk' ? intl.locale : 'en'],
      },
    );
  }
};

const PlansScreenAdapter = () => {
  const intl = useIntl();

  const { plans, discountPercentage, isLoading } = useAppSelector((state) => ({
    discountPercentage: state.coupon.discountPercentage || state.plans.displayedPlans?.[0]?.savePercentage,
    plans: state.plans.displayedPlans,
    isLoading: state.isInitializing,
  }));

  const dispatch = useAppDispatch();
  const onPlanClick = (plan: Plan) => () => dispatch(selectPlanAndProceed(plan));

  const discountLabel = resolveDiscountLabel(intl, plans, discountPercentage);

  return <PlansScreen plans={plans} discountLabel={discountLabel} isLoading={isLoading} onPlanClick={onPlanClick} />;
};

export default PlansScreenAdapter;
